import type { AlertColor } from "@mui/material/Alert/Alert";
import { useQueryClient as useTanStackQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import useQueryClient from "../../api/query";
import { Action } from "../actions";
import {
  useUpdateClaim,
  useUpdateDenial,
  useUpdateStringifyClaim,
} from "../ClaimsDashboard/hooks";
import { useClaimsPostingPoll } from "../polling";
import { postPMSUpdateAndNotify } from "../posting";
import {
  applyPatientsAction,
  type PatientsCacheAction,
  patchProfileDob,
  profileKey,
  restoreProfile,
  snapshotProfile,
  usePatientProfile,
  useUpdatePatient,
} from "./hooks";
import PatientProfileView, {
  type PatientProfileTabs,
  type PayerFilter,
} from "./views/PatientProfileView";

interface PatientProfileContainerProps {
  updateSnackBar: (snackbar: { severity: AlertColor; message: string }) => void;
  practices: PracticeMessage[];
  supportedSources: SourceMessage[];
  patientId?: string;
  onClose?: () => void;
  onDenialUpdated?: () => void;
  initialTab?: PatientProfileTabs;
  payerFilter?: PayerFilter | null;
  excludeClaimColumns?: string[];
}

const NO_CLAIMS: ClaimWithProcedureAndPatientMessage[] = [];

export default function PatientProfileContainer({
  updateSnackBar,
  practices,
  supportedSources,
  patientId: patientIdProp,
  onClose,
  onDenialUpdated,
  initialTab,
  payerFilter,
  excludeClaimColumns,
}: PatientProfileContainerProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { patientId: routePatientId } = useParams();
  const patientId = patientIdProp ?? routePatientId;

  const queryClient = useQueryClient();
  const rqClient = useTanStackQueryClient();
  const updatePatientMutation = useUpdatePatient();
  const updateClaimMutation = useUpdateClaim();
  const updateDenialMutation = useUpdateDenial();
  const updateStringifyClaimMutation = useUpdateStringifyClaim();
  const [reloadedPatient, setReloadedPatient] = useState<{
    from: string | undefined;
    to: string;
  }>();
  const [triggerReload, setTriggerReload] = useState(false);
  const [activePayerFilter, setActivePayerFilter] = useState(
    payerFilter ?? null,
  );

  const profileId =
    reloadedPatient && reloadedPatient.from === patientId
      ? reloadedPatient.to
      : patientId;
  const profileQuery = usePatientProfile(
    profileId,
    activePayerFilter?.payerId ?? null,
  );
  const loadedPatient = profileQuery.isError
    ? undefined
    : profileQuery.data?.patient;
  const claims = profileQuery.data?.claims ?? NO_CLAIMS;

  const updateStore = (action: PatientsCacheAction) =>
    applyPatientsAction(rqClient, action);

  useClaimsPostingPoll(claims, queryClient, updateStore, updateSnackBar);

  // biome-ignore lint/correctness/useExhaustiveDependencies: adding updateSnackBar would re-fire the snackbar
  useEffect(() => {
    const { error } = profileQuery;
    if (!profileQuery.isError) return;
    if (error instanceof AxiosError && error.response?.data) {
      const responseBody = error.response.data;
      updateSnackBar({
        severity: "error",
        message:
          (typeof responseBody.detail === "string"
            ? responseBody.detail
            : responseBody.detail?.[0]?.msg) || "Failed to load the patient.",
      });
    } else {
      updateSnackBar({
        severity: "error",
        message: "Failed to load the patient.",
      });
    }
  }, [profileQuery.isError, profileQuery.error]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: adding navigate would change when this runs
  useEffect(() => {
    const delayInMilliseconds = 750;
    let timeoutId: ReturnType<typeof window.setTimeout>;

    if (triggerReload) {
      timeoutId = setTimeout(() => {
        const currentPage = 0;
        navigate(currentPage);
      }, delayInMilliseconds);
    }

    return () => clearTimeout(timeoutId);
  }, [triggerReload]);

  const loadPatientAndClaims = async (id: string) => {
    if (id !== profileId) setReloadedPatient({ from: patientId, to: id });
    await rqClient.invalidateQueries({ queryKey: profileKey(id) });
  };

  const clearPayerFilter = async () => {
    setActivePayerFilter(null);
  };

  const editPatient = async (patient: PatientMessage) => {
    const originalPatient = loadedPatient;
    const originalProfile = snapshotProfile(rqClient, profileId);

    try {
      const existingPatient = originalPatient as PatientMessage;
      const deltas = (
        Object.entries(patient) as [keyof PatientMessage, unknown][]
      ).reduce<Partial<PatientMessage>>(
        (acc, [key, value]) => {
          if (key === "practiceId" || key === "wieldyId") {
            // biome-ignore lint/suspicious/noExplicitAny: assigning through a generic key the types can't narrow
            (acc[key] as any) = value;
          } else if (value !== undefined && existingPatient[key] !== value) {
            // biome-ignore lint/suspicious/noExplicitAny: assigning through a generic key the types can't narrow
            (acc[key] as any) = value;
          }
          return acc;
        },
        {} as Partial<PatientMessage>,
      );
      if (Object.keys(deltas).length === 0) {
        updateSnackBar({ severity: "warning", message: `Nothing to update` });
        return;
      }
      const updatedWieldyId = await updatePatientMutation.mutateAsync(
        deltas as PatientMessage,
      );

      if (updatedWieldyId && updatedWieldyId !== originalPatient?.wieldyId) {
        updateSnackBar({
          severity: "success",
          message: "Successfully edited patient",
        });
        searchParams.delete("redirect");
        if (onClose) {
          await loadPatientAndClaims(updatedWieldyId);
          return;
        }
        navigate({
          pathname: `/patients/${updatedWieldyId}`,
          search: searchParams.toString(),
        });
        return;
      }

      const updatedClaims = claims
        .filter((claim) => claim.wieldyPatientId === patient.wieldyId)
        .map((claim) => ({
          ...claim,
          patientDob: patient.dob,
          Patient: claim.Patient.map((p) =>
            p.wieldyId === patient.wieldyId ? { ...p, dob: patient.dob } : p,
          ),
        }));

      patchProfileDob(rqClient, profileId, patient);

      updateSnackBar({
        severity: "success",
        message: "Successfully edited patient",
      });
      const redirect = searchParams.get("redirect");
      if (redirect && !onClose) {
        searchParams.delete("redirect");
        const to = { pathname: redirect, search: searchParams.toString() };
        navigate(to, { state: { updatedClaims } });
        return;
      }

      if (onClose) {
        await loadPatientAndClaims(patient.wieldyId);
      } else {
        setTriggerReload(true);
      }
    } catch (error: unknown) {
      restoreProfile(rqClient, originalProfile);

      if (error instanceof AxiosError && error.response?.data) {
        const responseBody = error.response.data;
        updateSnackBar({
          severity: "error",
          message:
            (typeof responseBody.detail === "string"
              ? responseBody.detail
              : responseBody.detail?.[0]?.msg) || "An unknown error occurred.",
        });
      } else {
        updateSnackBar({
          severity: "error",
          message: "An unknown error occurred.",
        });
      }
    }
  };

  const postToPMS = async (claim: ClaimWithProcedureAndPatientMessage) => {
    await postPMSUpdateAndNotify(
      claim,
      updateStore,
      queryClient,
      updateSnackBar,
    );
  };

  const onDone = async (
    claim: ClaimWithProcedureAndPatientMessage,
    claimWork: ClaimWork,
  ) => {
    try {
      updateStore({
        type: "UPDATE_CLAIM",
        claimToUpdate: { ...claim, ...claimWork },
      });
      await updateDenialMutation.mutateAsync({ claimWork });
    } catch (error: unknown) {
      if (error instanceof AxiosError && error.response?.data) {
        const responseBody = error.response.data;
        updateSnackBar({
          severity: "error",
          message:
            (typeof responseBody.detail === "string"
              ? responseBody.detail
              : responseBody.detail?.[0]?.msg) || "An unknown error occurred.",
        });
      } else {
        updateSnackBar({
          severity: "error",
          message: "An unknown error occurred.",
        });
      }
    }
  };

  const onDenialUpdate = async (
    claim: ClaimWithProcedureAndPatientMessage,
    denialWork: DenialWork,
  ) => {
    try {
      const isSelection =
        denialWork.confirmAction !== true &&
        denialWork.userAction !== undefined;
      updateStore({
        type: "UPDATE_CLAIM",
        claimToUpdate: {
          ...claim,
          ...(denialWork.userAction !== undefined && {
            userAction: denialWork.userAction,
          }),
          ...(denialWork.urgency !== undefined && {
            denialUrgency: denialWork.urgency,
          }),
          ...(isSelection && { actionSelectedAt: new Date().toISOString() }),
        },
      });
      await updateDenialMutation.mutateAsync({
        claimWork: { wieldyId: claim.wieldyId, practiceId: claim.practiceId },
        denialWork,
      });
      onDenialUpdated?.();
      const isConfirmedTransition =
        denialWork.confirmAction === true &&
        denialWork.userAction != null &&
        denialWork.userAction !== Action.CALL_PAYER;
      if (isConfirmedTransition && patientId) {
        await loadPatientAndClaims(patientId);
      }
    } catch (error: unknown) {
      if (error instanceof AxiosError && error.response?.data) {
        const responseBody = error.response.data;
        updateSnackBar({
          severity: "error",
          message:
            (typeof responseBody.detail === "string"
              ? responseBody.detail
              : responseBody.detail?.[0]?.msg) || "An unknown error occurred.",
        });
      } else {
        updateSnackBar({
          severity: "error",
          message: "An unknown error occurred.",
        });
      }
    }
  };

  const updateClaim = async (
    claim: ClaimWithProcedureAndPatientMessage,
    claimWork: ClaimWork,
  ) => {
    try {
      updateStore({
        type: "UPDATE_CLAIM",
        claimToUpdate: { ...claim, ...claimWork },
      });
      await updateClaimMutation.mutateAsync({ claim, claimWork });
    } catch (error: unknown) {
      if (error instanceof AxiosError && error.response?.data) {
        const responseBody = error.response.data;
        updateSnackBar({
          severity: "error",
          message:
            (typeof responseBody.detail === "string"
              ? responseBody.detail
              : responseBody.detail?.[0]?.msg) || "An unknown error occurred.",
        });
      } else {
        updateSnackBar({
          severity: "error",
          message: "An unknown error occurred.",
        });
      }
    }
  };

  const saveClaim = async (claim: ClaimMessage) => {
    const existingClaim = claims.find((c) => c.wieldyId === claim.wieldyId);
    if (!existingClaim) {
      return;
    }
    const deltas = (
      Object.entries(claim) as [keyof ClaimMessage, unknown][]
    ).reduce<Partial<ClaimMessage>>((acc, [key, value]) => {
      const existingValue =
        existingClaim[key] && key === "payer"
          ? (existingClaim[key] as string).toUpperCase()
          : existingClaim[key];
      if (value !== undefined && existingValue !== value) {
        acc[key] = value;
      }
      return acc;
    }, {});
    if (Object.keys(deltas).length === 0) {
      updateSnackBar({ severity: "warning", message: "Nothing to update" });
      return;
    }
    try {
      const wieldyId = await updateStringifyClaimMutation.mutateAsync({
        update: {
          ...deltas,
          wieldyId: claim.wieldyId,
          practiceId: claim.practiceId,
        },
        patch: { ...existingClaim, ...claim },
      });
      updateStore({
        type: "UPDATE_CLAIM",
        claimToUpdate: { ...existingClaim, ...claim },
      });
      updateSnackBar({
        severity: "success",
        message: `Successfully edited claim: ${wieldyId}`,
      });
    } catch (error: unknown) {
      if (error instanceof AxiosError && error.response?.data) {
        const responseBody = error.response.data;
        updateSnackBar({
          severity: "error",
          message:
            (typeof responseBody.detail === "string"
              ? responseBody.detail
              : responseBody.detail?.[0]?.msg) || "An unknown error occurred.",
        });
      } else {
        updateSnackBar({
          severity: "error",
          message: "An unknown error occurred.",
        });
      }
    }
  };

  return (
    <PatientProfileView
      patientId={patientId}
      onBack={onClose}
      patient={loadedPatient}
      patientLoading={profileQuery.isPending}
      claims={claims}
      claimsLoading={profileQuery.isPending}
      update={updateClaim}
      saveClaim={saveClaim}
      editPatient={editPatient}
      toPMS={postToPMS}
      updateSnackBar={updateSnackBar}
      practices={practices}
      supportedSources={supportedSources}
      onDone={onDone}
      onDenialUpdate={onDenialUpdate}
      initialTab={initialTab}
      payerFilter={activePayerFilter}
      onClearPayerFilter={clearPayerFilter}
      excludeClaimColumns={excludeClaimColumns}
    />
  );
}
